/**
 * Promise-based dialogs for the portal — `await dialogs.confirm({...})`,
 * `await dialogs.prompt({...})`, `await dialogs.alert({...})` — replacing
 * the browser's window.confirm / prompt / alert, which cannot be styled,
 * ignore the theme, and read like 1998.
 *
 * One host is mounted at the root (main.tsx); any component under it calls
 * `useDialogs()`. Requests queue, so two dialogs never stack. Outside a
 * provider (unit tests, storybooks) the hook falls back to the browser
 * dialogs so nothing breaks.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { ConfirmDialog } from './ConfirmDialog';
import { PromptDialog } from './PromptDialog';

export type ConfirmOptions = {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive: red primary, focus lands on Cancel. */
  danger?: boolean;
};
export type PromptOptions = {
  title: string;
  label?: string;
  body?: ReactNode;
  initial?: string;
  placeholder?: string;
  hint?: string;
  /** Keep '' as an answer instead of treating it as "no answer". */
  allowEmpty?: boolean;
  confirmLabel?: string;
};
export type AlertOptions = { title: string; body?: ReactNode; okLabel?: string };

export type Dialogs = {
  confirm: (o: ConfirmOptions) => Promise<boolean>;
  prompt: (o: PromptOptions) => Promise<string | null>;
  alert: (o: AlertOptions) => Promise<void>;
};

type Request = { id: number } & (
  | { kind: 'confirm'; o: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: 'prompt'; o: PromptOptions; resolve: (v: string | null) => void }
  | { kind: 'alert'; o: AlertOptions; resolve: () => void }
);

let nextId = 0;

const fallback: Dialogs = {
  confirm: async (o) => window.confirm(`${o.title}${o.body ? `\n\n${String(o.body)}` : ''}`),
  prompt: async (o) => window.prompt(o.title, o.initial ?? ''),
  alert: async (o) => {
    window.alert(`${o.title}${o.body ? `\n\n${String(o.body)}` : ''}`);
  },
};

const Ctx = createContext<Dialogs>(fallback);

export function useDialogs(): Dialogs {
  return useContext(Ctx);
}

export function DialogProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Request[]>([]);
  const active = queue[0];

  const enqueue = useCallback(
    (r: Omit<Request, 'id'>) => setQueue((q) => [...q, { ...r, id: ++nextId } as Request]),
    [],
  );
  const finish = useCallback(() => setQueue((q) => q.slice(1)), []);

  const api = useMemo<Dialogs>(
    () => ({
      confirm: (o) => new Promise<boolean>((resolve) => enqueue({ kind: 'confirm', o, resolve })),
      prompt: (o) => new Promise<string | null>((resolve) => enqueue({ kind: 'prompt', o, resolve })),
      alert: (o) => new Promise<void>((resolve) => enqueue({ kind: 'alert', o, resolve })),
    }),
    [enqueue],
  );

  return (
    <Ctx.Provider value={api}>
      {children}
      {active?.kind === 'confirm' ? (
        <ConfirmDialog
          title={active.o.title}
          body={active.o.body}
          confirmLabel={active.o.confirmLabel ?? 'OK'}
          cancelLabel={active.o.cancelLabel ?? 'Cancel'}
          danger={Boolean(active.o.danger)}
          onConfirm={() => {
            active.resolve(true);
            finish();
          }}
          onCancel={() => {
            active.resolve(false);
            finish();
          }}
        />
      ) : null}
      {active?.kind === 'prompt' ? (
        <PromptDialog
          key={active.id}
          title={active.o.title}
          label={active.o.label}
          body={active.o.body}
          initial={active.o.initial ?? ''}
          placeholder={active.o.placeholder}
          hint={active.o.hint}
          allowEmpty={Boolean(active.o.allowEmpty)}
          confirmLabel={active.o.confirmLabel ?? 'OK'}
          onSubmit={(v) => {
            active.resolve(v);
            finish();
          }}
          onCancel={() => {
            active.resolve(null);
            finish();
          }}
        />
      ) : null}
      {active?.kind === 'alert' ? (
        <ConfirmDialog
          title={active.o.title}
          body={active.o.body}
          confirmLabel={active.o.okLabel ?? 'OK'}
          cancelLabel={null}
          danger={false}
          onConfirm={() => {
            active.resolve();
            finish();
          }}
          onCancel={() => {
            active.resolve();
            finish();
          }}
        />
      ) : null}
    </Ctx.Provider>
  );
}
